import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Form, Input, Button, Card, Typography, Radio, Space, message, Alert } from 'antd';
import { MailOutlined, MobileOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { authApi } from '@/services/api';

const { Title, Text } = Typography;

const ForgotPassword: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [type, setType] = useState<'email' | 'phone'>('email');
  const [loading, setLoading] = useState(false);
  const [resetToken, setResetToken] = useState('');

  const handleSubmit = async (values: { identifier: string }) => {
    setLoading(true);
    try {
      const response = await authApi.forgotPassword(values.identifier, type);

      // Development mode - show reset token
      if (import.meta.env.DEV && response.data.reset_token) {
        setResetToken(response.data.reset_token);
        message.success('开发模式：重置令牌已生成');
      } else {
        message.success(
          type === 'email' ? '重置链接已发送到您的邮箱，请查收' : '重置验证码已发送到您的手机，请查收'
        );
        setTimeout(() => {
          navigate('/reset-password');
        }, 2000);
      }
    } catch (error: any) {
      message.error(error.response?.data?.detail || '发送失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  const handleNavigateToReset = () => {
    if (resetToken) {
      navigate(`/reset-password?token=${resetToken}`);
    }
  };

  return (
    <div className="login-container">
      <div className="login-background">
        <div className="login-gradient" />
      </div>

      <div className="login-content">
        <Card className="login-card" style={{ maxWidth: '500px' }}>
          <div style={{ marginBottom: '24px' }}>
            <Link to="/login" style={{ fontSize: '14px', display: 'flex', alignItems: 'center' }}>
              <ArrowLeftOutlined style={{ marginRight: '4px' }} />
              返回登录
            </Link>
          </div>

          <div className="login-header">
            <Title level={2} className="login-title">
              忘记密码
            </Title>
            <Text type="secondary" className="login-subtitle">
              选择重置方式
            </Text>
          </div>

          <Form
            form={form}
            onFinish={handleSubmit}
            layout="vertical"
            size="large"
            style={{ marginTop: '32px' }}
          >
            <Form.Item label="重置方式">
              <Radio.Group value={type} onChange={(e) => setType(e.target.value)} buttonStyle="solid">
                <Radio.Button value="email">
                  <MailOutlined /> 邮箱
                </Radio.Button>
                <Radio.Button value="phone">
                  <MobileOutlined /> 手机号
                </Radio.Button>
              </Radio.Group>
            </Form.Item>

            <Form.Item
              name="identifier"
              label={type === 'email' ? '邮箱地址' : '手机号'}
              rules={[
                { required: true, message: type === 'email' ? '请输入邮箱地址' : '请输入手机号' },
                type === 'email'
                  ? { type: 'email', message: '请输入有效的邮箱地址' }
                  : { pattern: /^1[3-9]\d{9}$/, message: '请输入有效的手机号' },
              ]}
            >
              <Input
                prefix={type === 'email' ? <MailOutlined /> : <MobileOutlined />}
                placeholder={type === 'email' ? '请输入邮箱地址' : '请输入手机号'}
                autoComplete={type === 'email' ? 'email' : 'tel'}
              />
            </Form.Item>

            {resetToken && (
              <Alert
                type="info"
                message="开发模式：重置令牌"
                description={
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Text code copyable>
                      {resetToken}
                    </Text>
                    <Button type="link" onClick={handleNavigateToReset} style={{ padding: 0 }}>
                      点击前往重置密码页面
                    </Button>
                  </Space>
                }
                style={{ marginBottom: '16px' }}
              />
            )}

            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading} block size="large">
                {type === 'email' ? '发送重置链接' : '发送验证码'}
              </Button>
            </Form.Item>
          </Form>

          <div style={{ textAlign: 'center', marginTop: '16px' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              提示：
              {type === 'email'
                ? '重置链接将发送到您注册时使用的邮箱'
                : '验证码将发送到您注册时绑定的手机号'}
            </Text>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPassword;
